/**
 * Embed Communicator
 * Handles postMessage communication when funnel is embedded in an iframe
 *
 * Events FROM iframe TO parent:
 * - 1mf-ready: Funnel loaded and ready
 * - 1mf-resize: Height changed
 * - 1mf-scroll: Request parent to scroll
 * - 1mf-progress: Quiz progress update
 * - 1mf-answer: Question answered
 * - 1mf-complete: Quiz completed (final page)
 * - 1mf-submit: Form submitted
 * - 1mf-redirect: Redirect triggered
 * - 1mf-error: Error occurred
 * - 1mf-smart-path: Smart path triggered
 *
 * Commands FROM parent TO iframe:
 * - 1mf-reset: Reset quiz
 * - 1mf-prefill: Prefill form fields
 * - 1mf-setUTM: Set UTM parameters
 * - 1mf-setCustomData: Set custom lead data
 */

import { createLogger } from '../core/logger';
import { getConfig } from '../core/config';
import { getState, clearProgress } from '../core/state';

const logger = createLogger('Embed');

let funnelID: string | null = null;
let isEmbedded = false;
let resizeObserver: ResizeObserver | null = null;
let lastHeight = 0;

// Custom data to merge into lead
let customLeadData: Record<string, unknown> = {};

// Prefilled form values
let prefilledFields: Record<string, string | null> = {};

/**
 * Check if we're running in an iframe (embedded)
 */
function checkIfEmbedded(): boolean {
  try {
    // Check if in iframe
    isEmbedded = window.self !== window.top;

    // Also check for ?embed=true parameter
    const config = getConfig();
    if (config.urlParams.get('embed') === 'true') {
      isEmbedded = true;
    }

    return isEmbedded;
  } catch {
    // If we can't access window.top, we're definitely in a cross-origin iframe
    isEmbedded = true;
    return true;
  }
}

/**
 * Send message to parent window
 */
function sendMessage(type: string, data: Record<string, unknown> = {}): void {
  if (!isEmbedded) return;

  const message = {
    type,
    funnelID,
    ...data
  };

  try {
    window.parent.postMessage(message, '*');
    logger.debug('Sent message to parent', message);
  } catch (error) {
    logger.warn('Failed to send postMessage', error);
  }
}

/**
 * Send resize event with current document height
 */
export function sendResize(): void {
  if (!isEmbedded) return;

  // Get actual content height
  const contentHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight
  );

  // On mobile, cap height to prevent infinite scroll
  const isMobile = window.innerWidth <= 768;
  const maxHeight = isMobile ? 650 : 900;
  const minHeight = 400;

  const height = Math.min(Math.max(contentHeight, minHeight), maxHeight);

  // Only send if height changed significantly (avoid spam)
  if (Math.abs(height - lastHeight) > 10) {
    lastHeight = height;
    sendMessage('1mf-resize', { height });
  }
}

/**
 * Send scroll event (tells parent to scroll to quiz)
 */
export function sendScroll(): void {
  sendMessage('1mf-scroll');
}

/**
 * Send redirect event (for final page redirects)
 */
export function sendRedirect(url: string): void {
  sendMessage('1mf-redirect', { url });
}

/**
 * Send ready event (funnel loaded)
 */
export function sendReady(): void {
  const state = getState();
  sendMessage('1mf-ready', {
    funnelID,
    version: state.funnel?.version
  });
}

/**
 * Send progress event
 */
export function sendProgress(questionIndex: number, totalQuestions: number): void {
  sendMessage('1mf-progress', {
    questionIndex,
    totalQuestions,
    percent: totalQuestions > 0 ? Math.round((questionIndex / totalQuestions) * 100) : 0
  });
}

/**
 * Send answer event
 */
export function sendAnswer(questionId: string, optionValue: string, optionLabel: string): void {
  sendMessage('1mf-answer', {
    questionId,
    answer: optionValue,
    answerLabel: optionLabel
  });
}

/**
 * Send complete event (reached final page)
 */
export function sendComplete(): void {
  sendMessage('1mf-complete', {});
}

/**
 * Send submit event (form submitted)
 */
export function sendSubmit(success: boolean, leadId: string | null, error?: string): void {
  sendMessage('1mf-submit', {
    success,
    leadId,
    error: error || null
  });
}

/**
 * Send error event
 */
export function sendError(errorType: string, message: string): void {
  sendMessage('1mf-error', {
    errorType,
    message
  });
}

/**
 * Send smart path event
 */
export function sendSmartPath(pathId: string, pathType: string, action: string): void {
  sendMessage('1mf-smart-path', {
    pathId,
    pathType,
    action
  });
}

/**
 * Handle incoming command from parent
 */
function handleParentCommand(event: MessageEvent): void {
  const data = event.data;

  if (!data || typeof data.type !== 'string' || !data.type.startsWith('1mf-')) {
    return; // Not our message
  }

  logger.debug('Received command from parent', data);

  switch (data.type) {
    case '1mf-reset':
      handleResetCommand();
      break;

    case '1mf-prefill':
      handlePrefillCommand(data);
      break;

    case '1mf-setUTM':
      handleSetUTMCommand(data);
      break;

    case '1mf-setCustomData':
      handleSetCustomDataCommand(data);
      break;

    default:
      logger.debug('Unknown command', data.type);
  }
}

/**
 * Handle reset command - reset quiz to beginning
 */
function handleResetCommand(): void {
  logger.info('Reset command received from parent');
  clearProgress();
  window.location.reload();
}

/**
 * Handle prefill command - pre-fill form fields
 */
function handlePrefillCommand(data: Record<string, unknown>): void {
  logger.info('Prefill command received', data);

  prefilledFields = {
    email: (data.email as string) || null,
    phone: (data.phone as string) || null,
    name: (data.name as string) || null,
    firstName: (data.firstName as string) || null,
    lastName: (data.lastName as string) || null
  };

  // If form is already rendered, fill the fields
  applyPrefill();
}

/**
 * Apply prefilled values to form fields
 */
export function applyPrefill(): void {
  const fieldMappings: Record<string, string[]> = {
    email: ['contact-email', 'email'],
    phone: ['contact-phone', 'phone'],
    name: ['contact-name', 'name'],
    firstName: ['contact-first-name', 'firstName'],
    lastName: ['contact-last-name', 'lastName']
  };

  for (const [key, ids] of Object.entries(fieldMappings)) {
    const value = prefilledFields[key];
    if (!value) continue;

    for (const id of ids) {
      const field = document.getElementById(id) as HTMLInputElement;
      if (field) {
        field.value = value;
        break;
      }
    }
  }

  logger.debug('Prefilled fields applied');
}

/**
 * Handle setUTM command - set UTM parameters
 */
function handleSetUTMCommand(data: Record<string, unknown>): void {
  logger.info('setUTM command received', data);

  const utmParams: Record<string, string> = {};
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  for (const key of utmKeys) {
    if (data[key]) {
      utmParams[key] = data[key] as string;
    }
  }

  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('embed_utm_params', JSON.stringify(utmParams));
    logger.debug('UTM params stored from parent', utmParams);
  }
}

/**
 * Handle setCustomData command - set custom data to merge into lead
 */
function handleSetCustomDataCommand(data: Record<string, unknown>): void {
  logger.info('setCustomData command received', data);
  customLeadData = { ...customLeadData, ...(data.data as Record<string, unknown>) };
  logger.debug('Custom lead data updated', customLeadData);
}

/**
 * Start observing for height changes
 */
function startObserving(): void {
  if (!isEmbedded) return;

  // Send initial height
  setTimeout(sendResize, 100);

  // Use ResizeObserver for efficient height tracking
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      sendResize();
    });

    resizeObserver.observe(document.body);
    logger.debug('ResizeObserver started');
  } else {
    // Fallback: poll for height changes
    setInterval(sendResize, 500);
    logger.debug('Using polling fallback for resize detection');
  }

  // Also send resize on window resize
  window.addEventListener('resize', sendResize);

  // Send resize after images load
  window.addEventListener('load', () => {
    setTimeout(sendResize, 100);
  });
}

/**
 * Initialize embed communicator
 */
export function initEmbed(): boolean {
  const config = getConfig();
  funnelID = config.funnelName;

  checkIfEmbedded();

  if (isEmbedded) {
    logger.info('Running in embedded mode', { funnelID });

    // Add body class for embed-specific CSS
    document.body.classList.add('is-embedded');
    if (window.innerWidth <= 768) {
      document.body.classList.add('is-embedded-mobile');
    }

    // Start listening for parent commands
    window.addEventListener('message', handleParentCommand);

    // Start observing for height changes
    startObserving();

    // Send ready event after short delay
    setTimeout(sendReady, 100);
  } else {
    logger.debug('Not embedded, postMessage disabled');
  }

  return isEmbedded;
}

/**
 * Check if currently embedded
 */
export function isEmbeddedMode(): boolean {
  return isEmbedded;
}

/**
 * Get prefilled fields
 */
export function getPrefilledFields(): Record<string, string | null> {
  return prefilledFields;
}

/**
 * Get custom lead data
 */
export function getCustomLeadData(): Record<string, unknown> {
  return customLeadData;
}

/**
 * Notify that a question transition happened
 */
export function onQuestionChange(): void {
  sendScroll();
  // Small delay then send resize in case content changed
  setTimeout(sendResize, 350);
}

/**
 * Cleanup
 */
export function destroyEmbed(): void {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  window.removeEventListener('message', handleParentCommand);
  window.removeEventListener('resize', sendResize);
}
